import React, { ReactNode } from 'react';

import styled from 'styled-components';

import { useMobileContext } from '../MobileContext';

export interface ModalFormLayoutProps {
  /** Form content */
  children: ReactNode;
  /** Modal title */
  title?: ReactNode;
  /** Modal header content (replaces title if provided) */
  header?: ReactNode;
  /** Footer actions */
  footer?: ReactNode;
  /** Custom className */
  className?: string;
  /** Form submission handler */
  onSubmit?: (event: React.FormEvent) => void;
  /** Modal configuration */
  modalConfig?: {
    /** Maximum width of modal */
    maxWidth?: string;
    /** Maximum height of modal */
    maxHeight?: string;
    /** Show close button */
    showCloseButton?: boolean;
    /** Compact layout for mobile */
    compactOnMobile?: boolean;
  };
  /** Loading state */
  isLoading?: boolean;
  /** Error message */
  error?: string;
}

/**
 * Standardized modal form layout component that replaces custom modal styling.
 * Provides consistent modal behavior with proper form structure.
 *
 * Features:
 * - Responsive modal sizing
 * - Form validation support
 * - Loading and error states
 * - Consistent header/footer layout
 * - Mobile-optimized spacing
 * - Accessible keyboard navigation
 *
 * @example
 * // Patient selection modal
 * <ModalFormLayout
 *   title="Select Patient"
 *   footer={
 *     <ActionBarLayout align="end">
 *       <Button onClick={onCancel}>Cancel</Button>
 *       <Button type="submit" variant="primary">Select</Button>
 *     </ActionBarLayout>
 *   }
 *   onSubmit={handleSelectPatient}
 * >
 *   <PatientSearchForm />
 * </ModalFormLayout>
 *
 * @example
 * // Upload modal with custom header
 * <ModalFormLayout
 *   header={<CustomUploadHeader />}
 *   modalConfig={{ maxWidth: '800px', compactOnMobile: true }}
 * >
 *   <FileUploadForm />
 * </ModalFormLayout>
 */
export const ModalFormLayout: React.FC<ModalFormLayoutProps> = ({
  children,
  title,
  header,
  footer,
  className,
  onSubmit,
  modalConfig = {},
  isLoading = false,
  error,
}) => {
  const { isMobile } = useMobileContext();

  const { maxWidth = '600px', maxHeight = '90vh', showCloseButton = true, compactOnMobile = false } = modalConfig;

  const handleSubmit = (event: React.FormEvent) => {
    if (onSubmit && !isLoading) {
      onSubmit(event);
    }
  };

  return (
    <StyledModalFormLayout className={className} $isMobile={isMobile} $compactOnMobile={compactOnMobile} $maxWidth={maxWidth} $maxHeight={maxHeight}>
      {/* Header Section */}
      {(header || title) && (
        <StyledModalHeader $isMobile={isMobile} $compactOnMobile={compactOnMobile}>
          {header || <StyledModalTitle>{title}</StyledModalTitle>}
          {showCloseButton && (
            <StyledCloseButton type="button" aria-label="Close modal">
              ×
            </StyledCloseButton>
          )}
        </StyledModalHeader>
      )}

      {/* Error Display */}
      {error && (
        <StyledErrorContainer $isMobile={isMobile}>
          <StyledErrorMessage>{error}</StyledErrorMessage>
        </StyledErrorContainer>
      )}

      {/* Form Content */}
      <StyledFormWrapper
        as={onSubmit ? 'form' : 'div'}
        onSubmit={onSubmit ? handleSubmit : undefined}
        $isMobile={isMobile}
        $compactOnMobile={compactOnMobile}
        $isLoading={isLoading}
      >
        <StyledModalContent $isMobile={isMobile} $compactOnMobile={compactOnMobile}>
          {children}
        </StyledModalContent>

        {/* Footer Section */}
        {footer && (
          <StyledModalFooter $isMobile={isMobile} $compactOnMobile={compactOnMobile}>
            {footer}
          </StyledModalFooter>
        )}
      </StyledFormWrapper>

      {/* Loading Overlay */}
      {isLoading && (
        <StyledLoadingOverlay>
          <StyledLoadingSpinner>Loading...</StyledLoadingSpinner>
        </StyledLoadingOverlay>
      )}
    </StyledModalFormLayout>
  );
};

ModalFormLayout.displayName = 'ModalFormLayout';

// Styled Components
interface StyledProps {
  $isMobile: boolean;
  $compactOnMobile?: boolean;
  $maxWidth?: string;
  $maxHeight?: string;
  $isLoading?: boolean;
}

const StyledModalFormLayout = styled.div<StyledProps>`
  /* Modal container styling */
  background-color: ${({ theme }) => theme.colors.palette.white};
  border-radius: ${({ theme }) => theme.radii.scale[2]};
  box-shadow: ${({ theme }) => theme.shadows.primary};

  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth};
  max-height: ${({ $maxHeight }) => $maxHeight};

  display: grid;
  grid-template-rows: auto auto 1fr;

  position: relative;
  overflow: hidden;

  /* Mobile adjustments */
  @media (max-width: 768px) {
    ${({ $compactOnMobile }) =>
      $compactOnMobile &&
      `
      max-width: calc(100vw - 20px);
      max-height: calc(100vh - 40px);
      border-radius: 8px;
    `}
  }
`;

const StyledModalHeader = styled.div<StyledProps>`
  /* Header layout */
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: ${({ theme, $isMobile, $compactOnMobile }) => {
    if ($isMobile && $compactOnMobile) {
      return `${theme.space.spacing10} ${theme.space.spacing10}`;
    }
    return `${theme.space.spacing20} ${theme.space.spacing20}`;
  }};

  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};
`;

const StyledModalTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.heading2};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StyledCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radii.scale[1]};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.palette.grayscale[1]};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const StyledErrorContainer = styled.div<StyledProps>`
  padding: ${({ theme, $isMobile }) => ($isMobile ? theme.space.spacing10 : theme.space.spacing20)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
`;

const StyledErrorMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.palette.reds[0]};
  color: ${({ theme }) => theme.colors.palette.reds[7]};
  padding: ${({ theme }) => theme.space.spacing10};
  border-radius: ${({ theme }) => theme.radii.scale[1]};
  border: 1px solid ${({ theme }) => theme.colors.palette.reds[2]};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
`;

const StyledFormWrapper = styled.div<StyledProps>`
  /* Form container that fills remaining space */
  display: grid;
  grid-template-rows: 1fr auto;
  min-height: 0;

  ${({ $isLoading }) =>
    $isLoading &&
    `
    pointer-events: none;
    opacity: 0.7;
  `}
`;

const StyledModalContent = styled.div<StyledProps>`
  /* Scrollable content area */
  padding: ${({ theme, $isMobile, $compactOnMobile }) => {
    if ($isMobile && $compactOnMobile) {
      return `${theme.space.spacing10} ${theme.space.spacing10}`;
    }
    return `${theme.space.spacing20} ${theme.space.spacing20}`;
  }};

  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;

  /* Smooth scrolling on iOS */
  -webkit-overflow-scrolling: touch;
`;

const StyledModalFooter = styled.div<StyledProps>`
  /* Footer with actions */
  padding: ${({ theme, $isMobile, $compactOnMobile }) => {
    if ($isMobile && $compactOnMobile) {
      return `${theme.space.spacing10} ${theme.space.spacing10}`;
    }
    return `${theme.space.spacing20} ${theme.space.spacing20}`;
  }};

  border-top: 1px solid ${({ theme }) => theme.colors.borderBase};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};
`;

const StyledLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const StyledLoadingSpinner = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.body};
`;
